import '@toasttab/main/src/assets/styles/global.css'
import 'lazysizes'
import React, { Suspense } from 'react'
import { wrapPageElement as SharedWrapPageElement } from '@toasttab/shared/src/page/wrapPageElement'
import NinetailedHashScroll from './src/components/NinetailedHashScroll'
import { sha256encode } from '@toasttab/shared/src/utils/dataCaptureUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { anchorLinkScroll } from '@toasttab/shared/src/utils/linkUtils'
import { ensureNoterefClickTracking } from '@toasttab/shared/src/utils/footnotes/footnoteDom'
import { deferredStylesheetsLoaded } from './src/utils/criticalCss'
import { trackPageViewed, trackCtaClicked } from './src/utils/serverTracking'

export const disableCorePrefetching = () => true

const ComponentInspectorProvider =
  process.env.NODE_ENV === 'development'
    ? require('@toasttab/shared/src/components/ComponentInspector/ComponentInspectorModal')
        .ComponentInspectorProvider
    : ({ children }: { children: React.ReactNode }) => <>{children}</>

export const wrapRootElement = ({ element }: any) => {
  const enableContentfulLivePreview =
    process.env.GATSBY_CONTENTFUL_LIVE_PREVIEW_ENABLE !== 'false'

  // Get locale from query string (only runs in browser)
  let locale = 'en'
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    locale = params.get('locale') || 'en'
  }

  if (enableContentfulLivePreview) {
    // If deployed to a preview site, load the ContentfulLivePreviewProvider bundle
    const ContentfulLivePreviewProvider = React.lazy(() =>
      import(
        /* webpackChunkName: "vendor-contentful-live-preview" */
        '@contentful/live-preview/react'
      ).then((m) => ({
        default: m.ContentfulLivePreviewProvider
      }))
    )
    const App = () => (
      <Suspense fallback={null}>
        <ContentfulLivePreviewProvider
          locale={String(locale)}
          enableInspectorMode={true}
          enableLiveUpdates={true}
          debugMode={false}
        >
          <ComponentInspectorProvider>{element}</ComponentInspectorProvider>
        </ContentfulLivePreviewProvider>
      </Suspense>
    )
    return <App />
  } else {
    const App = () => (
      <ComponentInspectorProvider>{element}</ComponentInspectorProvider>
    )
    return <App />
  }
}

const inspectorWrapPageElement =
  process.env.NODE_ENV === 'development'
    ? require('@toasttab/shared/src/components/ComponentInspector/ComponentInspectorWrapPage')
        .wrapPageElement
    : SharedWrapPageElement

export const wrapPageElement = (args: any, pluginOptions: any) => (
  <>
    <NinetailedHashScroll />
    {inspectorWrapPageElement(args, pluginOptions)}
  </>
)

export const onClientEntry = () => {
  if (typeof window === 'undefined') return // Ensure this runs only in the browser

  // Hand scroll control to our hook; prevents the browser from firing its own
  // native hash-scroll on initial load, which would race with Ninetailed rendering.
  window.history.scrollRestoration = 'manual'

  ensureNoterefClickTracking()

  // Intercept doc-noteref clicks before Gatsby's catch-links can treat them as
  // route changes (which triggers a full page re-render + flash).
  document.addEventListener(
    'click',
    (e: MouseEvent) => {
      const target = (e.target as Element)?.closest('a[role="doc-noteref"]')
      if (!target) return
      const href = (target as HTMLAnchorElement).getAttribute('href')
      if (!href?.startsWith('#')) return
      e.preventDefault()
      e.stopPropagation()
      anchorLinkScroll(href)
    },
    true
  )

  document.addEventListener('click', (e: MouseEvent) => {
    const cta = (e.target as Element).closest('a, button') as HTMLElement | null
    if (!cta) return
    const label = cta.textContent?.trim() ?? ''
    const ctaDataEventId =
      cta.dataset.testid ?? cta.dataset.interaction ?? cta.id ?? ''
    const href = (cta as HTMLAnchorElement).href ?? ''
    trackCtaClicked(label, ctaDataEventId, href)
  })

  if (window.Fides?.initialized) {
    // To make sure this script loads if `FidesInitialized` was called
    // before your script loader runs.
    conditionallyLoadScripts()
  } else {
    // To make sure the script loads if your script loader ran before
    // Fides was initialized.
    addEventListener('FidesInitialized', conditionallyLoadScripts)
  }

  // If the user is opted out, and then opts in, make sure to run your script loader.
  addEventListener('FidesUpdated', conditionallyLoadScripts)

  // @ts-ignore
  window.dataLayer = window.dataLayer || []

  const initGTMOnEvent = (event: any) => {
    initGTM()
    event.currentTarget.removeEventListener(event.type, initGTMOnEvent) // Remove the triggered event listener
  }

  const initGTM = () => {
    // @ts-ignore
    if (window.gtmDidInit) {
      return false // Ensure the script is not added multiple times
    }
    // @ts-ignore
    window.gtmDidInit = true

    // Create and append the GTM script
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.async = true
    script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-MNHK89W'

    script.onload = () => {
      // @ts-ignore
      window.dataLayer.push({
        event: 'gtm.js',
        'gtm.start': new Date().getTime(),
        'gtm.uniqueEventId': 0
      }) // Ensure PageViews are tracked
    }

    // MKE-3149: Add SHA256 hashed email and phone number to dataLayer for GTM tracking pixel
    if (window.Fides?.consent?.analytics) {
      if (_helpers.getCookie('email') || _helpers.getCookie('phonenumber')) {
        Promise.all([
          sha256encode(_helpers.getCookie('email')),
          sha256encode(_helpers.getCookie('phonenumber'))
        ]).then(([encodedEmail, encodedPhone]) => {
          // @ts-ignore
          window.dataLayer.push({
            user_data: {
              sha256_email_address: encodedEmail,
              sha256_phone_number: encodedPhone
            }
          })
        })
      }
    }

    document.head.appendChild(script)
  }

  // Add event listeners
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initGTM, 3500) // Initialize GTM after 3500ms
  })

  document.addEventListener('scroll', initGTMOnEvent)
  document.addEventListener('mousemove', initGTMOnEvent)
  document.addEventListener('touchstart', initGTMOnEvent)

  // Beasties defers the full stylesheet (media="print" until onload); hold hydration
  // until it applies so client-rendered UI (banners, variants) never paints unstyled.
  return deferredStylesheetsLoaded()
}

function conditionallyLoadScripts() {
  // Check the relevant consent flags
  if (window.Fides?.consent?.analytics) {
    // Load script(s)
    loadHeapAnalytics()
    loadDataDog()
  }

  if (window.Fides?.consent?.marketing) {
    loadImpactRadiusTracking()
  }
}

function getDataDogEnvironment() {
  const url = process.env.GATSBY_CMS_URL
    ? process.env.GATSBY_CMS_URL.replace(/(^\w+:|^)\/\//, '')
    : 'pos.toasttab.com'

  switch (true) {
    case url.includes('pos'):
      return 'production'
    case url.includes('staging'):
      return 'staging'
    case url.includes('dev'):
      return 'development'
    default:
      return 'development'
  }
}

function loadDataDog() {
  if (!window.Fides?.consent?.analytics) {
    // @ts-ignore
    if (!!window?.DD_RUM) {
      // @ts-ignore
      window.DD_RUM.setTrackingConsent(getDataDogTrackingConsent())
    }
    return
  }

  if (!!window?.DD_RUM) {
    return
  }

  loadScript(
    'https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum.js',
    () => {
      // @ts-ignore
      window.DD_RUM.onReady(function () {
        const env = getDataDogEnvironment()
        // @ts-ignore
        window.DD_RUM.setTrackingConsent(getDataDogTrackingConsent())
        // @ts-ignore
        window.DD_RUM.init({
          clientToken: process.env.GATSBY_DATADOG_CLIENT_TOKEN || '',
          applicationId: process.env.GATSBY_DATADOG_APP_ID || '',
          service: 'mktg-toasttab-com-headless',
          env,
          site: 'datadoghq.com',
          sessionSampleRate: 1,
          sessionReplaySampleRate: 0, // if not included, the default is 100
          trackResources: true,
          trackLongTasks: true,
          trackUserInteractions: true,
          enablePrivacyForActionName: true
        })
        // Add global context fields for attribution
        // @ts-ignore
        window.DD_RUM.setGlobalContextProperty(
          'toast_service_name',
          'mktg-toasttab-com-headless'
        )
        // @ts-ignore
        window.DD_RUM.setGlobalContextProperty('toast_environment', env)
      })
    }
  )
}

function getDataDogTrackingConsent() {
  return window.Fides?.consent?.analytics ? 'granted' : 'not-granted'
}

function loadHeapAnalytics() {
  if (!window.Fides?.consent?.analytics) {
    if (!!window?.heap) {
      window.heap.stopTracking()
      window.heap.clearUserProperties()
      window.heap.resetIdentity()
    }
    return
  }

  if (!!window?.heap) {
    return
  }

  const appId = window.location.origin.includes('pos.toasttab.com')
    ? '1227582568'
    : '4031532897'

  // @ts-ignore
  ;(window.heap = window.heap || []),
    // @ts-ignore
    (window.heapReadyCb = window.heapReadyCb || []),
    (window.heap = window.heap || []),
    // @ts-ignore
    (heap.load = function (e, t) {
      ;(window.heap.envId = e),
        (window.heap.clientConfig = t = t || {}),
        (window.heap.clientConfig.shouldFetchServerConfig = !1)
      var a = document.createElement('script')
      ;(a.type = 'text/javascript'),
        (a.async = !0),
        (a.src = 'https://cdn.us.heap-api.com/config/' + e + '/heap_config.js')
      var r = document.getElementsByTagName('script')[0]
      r.parentNode.insertBefore(a, r)
      var n = [
          'init',
          'startTracking',
          'stopTracking',
          'track',
          'resetIdentity',
          'identify',
          'getSessionId',
          'getUserId',
          'getIdentity',
          'addUserProperties',
          'addEventProperties',
          'removeEventProperty',
          'clearEventProperties',
          'addAccountProperties',
          'addAdapter',
          'addTransformer',
          'addTransformerFn',
          'onReady',
          'addPageviewProperties',
          'removePageviewProperty',
          'clearPageviewProperties',
          'trackPageview'
        ],
        i = function (e: string) {
          return function () {
            var t = Array.prototype.slice.call(arguments, 0)
            // @ts-ignore
            window.heapReadyCb.push({
              name: e,
              fn: function () {
                // @ts-ignore
                heap[e] && heap[e].apply(heap, t)
              }
            })
          }
        }
      // @ts-ignore
      for (var p = 0; p < n.length; p++) heap[n[p]] = i(n[p])
    })

  // Initialize the library with your `appId`.
  // @ts-ignore
  heap.load(appId)
}

function loadImpactRadiusTracking() {
  if (typeof window.ire === 'function') {
    // already loaded, so don't load again
    return
  }

  loadScript(
    'https://utt.impactcdn.com/A2526552-22b7-4399-83ee-06f98f5f67921.js',
    () => {
      window?.ire?.('identify', {
        customerid: '',
        customeremail: ''
      })
    }
  )
}

export const onRouteUpdate = ({ location }: { location: Location }) => {
  trackPageViewed(derivePageType(location.pathname))
}

function derivePageType(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'homepage'
  if (pathname.includes('/pricing')) return 'pricing'
  if (pathname.includes('/demo') || pathname.includes('/request-demo'))
    return 'demo'
  if (pathname.includes('/blog') || pathname.includes('/on-the-line'))
    return 'blog'
  return 'content'
}

function loadScript(src: string, callback?: () => void) {
  const script = document.createElement('script')
  script.src = src
  script.async = true
  document.head.appendChild(script)

  if (callback) {
    script.onload = callback
  }
}
